import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  ClockCounterClockwise,
  CaretDown,
  EnvelopeSimple,
  FileText,
  GoogleLogo,
  Play,
  Pause,
  SpeakerHigh,
  SpeakerSlash,
  CornersOut,
  PhoneCall,
  TrendUp,
  Target,
  ChatCircleDots,
  User,
  ShieldCheck,
  Megaphone,
  Info,
  GearSix,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import googleMeetLogo from "@/assets/google-meet-logo.svg";
import zoomLogo from "@/assets/zoom-logo.svg";
import microsoftTeamsLogo from "@/assets/microsoft-teams-logo.svg";
import landingHeroContextDesktop from "@/assets/landing-hero-customer-context-1600.webp";
import landingHeroContextMobile from "@/assets/landing-hero-customer-context-900.webp";
import PublicContactDialog from "@/components/PublicContactDialog";
import BrandLockup from "@/components/BrandLockup";
import PublicMarketingThemeControl, { usePublicMarketingTheme } from "@/components/PublicMarketingThemeControl";
import WhistleIcon from "@/components/WhistleIcon";
import ConversationHistoryTimeline from "@/components/ConversationHistoryTimeline";
import { DEMO_RELATIONSHIP_DATA } from "@/data/liveDemoData";
import { trackPublicEvent } from "@/lib/publicAnalytics";
import { isNumericScore, scoreToTone, scoreValueToRating } from "@/lib/scoreGrade";
import { useProgressiveSectionReveal } from "@/lib/progressiveSectionReveal";

const audienceOutputs = [
  { label: "Relationship memory", icon: ClockCounterClockwise, path: "/product/relationship-memory", eventId: "relationship_memory" },
  { label: "Notes & follow-ups", icon: FileText, path: "/product/crm-notes", eventId: "crm_notes" },
  { label: "Coaching", icon: WhistleIcon, path: "/product/coaching", eventId: "coaching" },
  { label: "Next-call prep", icon: PhoneCall, path: "/product/prepare-next-call", eventId: "prepare_next_call" },
];


export default function LandingPage() {
  const { user, loading } = useAuth();
  const landingPageRef = useRef(null);
  const { preference: marketingThemePreference, resolvedTheme: marketingResolvedTheme, setPreference: setMarketingThemePreference } = usePublicMarketingTheme();
  useProgressiveSectionReveal(landingPageRef, "main > section:not(#product-tour)");
  const [searchParams] = useSearchParams();
  const requestBeta = searchParams.get("request") === "beta";
  const requestedEmail = (searchParams.get("email") || "").trim().toLowerCase();
  const [contactDialog, setContactDialog] = useState(requestBeta ? "beta" : null);
  const landingHeroDesktopAsset = landingHeroContextDesktop;
  const landingHeroMobileAsset = landingHeroContextMobile;

  useEffect(() => {
    if (requestBeta) setContactDialog("beta");
  }, [requestBeta]);

  useEffect(() => {
    trackPublicEvent("page_view", { source: "landing", section: "page" });
  }, []);

  const closeContactDialog = useCallback(() => {
    setContactDialog(null);

    // Closing a modal must never trigger React Router navigation. Clean only the
    // beta-prefill parameters in place and leave the landing route untouched.
    if (typeof window === "undefined") return;
    const currentUrl = new URL(window.location.href);
    const hadRequest = currentUrl.searchParams.has("request");
    const hadEmail = currentUrl.searchParams.has("email");
    if (!hadRequest && !hadEmail) return;

    currentUrl.searchParams.delete("request");
    currentUrl.searchParams.delete("email");
    const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    window.history.replaceState(window.history.state, "", nextUrl);
  }, []);

  const trackSignUp = (section) => {
    trackPublicEvent("cta_click", {
      source: "landing",
      section,
      cta_id: "sign_up_free",
    });
  };

  const openSupportDialog = (section) => {
    trackPublicEvent("cta_click", {
      source: "landing",
      section,
      cta_id: "support",
    });
    setContactDialog("support");
  };

  const trackSignIn = (section) => {
    trackPublicEvent("cta_click", {
      source: "landing",
      section,
      cta_id: user ? "open_dashboard" : "sign_in",
    });
  };

  const trackDemo = (section) => {
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem("callsect:live-demo-stage");
      window.sessionStorage.removeItem("callsect:live-demo-use-case");
    }
    trackPublicEvent("cta_click", {
      source: "landing",
      section,
      cta_id: "live_product_demo",
    });
  };

  const scrollToProductTour = (event) => {
    event.preventDefault();
    trackPublicEvent("cta_click", {
      source: "landing",
      section: "hero-primary",
      cta_id: "see_how_it_works",
    });

    if (typeof document === "undefined") return;
    document.getElementById("product-tour")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const marketingThemeClass = marketingResolvedTheme === "dark"
    ? "public-fixed-dark dark marketing-theme-dark"
    : "marketing-theme-light";

  return (
    <div
      ref={landingPageRef}
      data-marketing-theme={marketingResolvedTheme}
      className={`${marketingThemeClass} public-marketing-theme marketing-shell landing-page min-h-screen overflow-hidden`}
    >
      <header className="marketing-header relative z-30 border-b px-5 py-4 backdrop-blur-xl sm:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <Link to="/" className="brand-lockup-link" aria-label="Callsect home">
            <BrandLockup className="brand-lockup--landing" variant="auto" />
          </Link>

          <div className="flex items-center gap-3">
            <PublicMarketingThemeControl value={marketingThemePreference} onChange={setMarketingThemePreference} />
            <span className="marketing-beta-chip hidden rounded-full border px-3 py-1 text-xs font-medium sm:inline-flex">
              Beta
            </span>
            {!loading && user ? (
              <Link
                to="/dashboard"
                onClick={() => trackSignIn("header")}
                className="marketing-header-action inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition"
              >
                Open dashboard
                <ArrowRight size={15} />
              </Link>
            ) : (
              <Link
                to="/login"
                onClick={() => trackSignIn("header")}
                className="marketing-header-link rounded-lg px-3 py-2 text-sm transition sm:px-4"
              >
                Sign in
              </Link>
            )}
          </div>
        </div>
      </header>

      <main>
        <section className="landing-hero-section relative overflow-hidden px-5 pb-16 pt-16 sm:px-8 sm:pb-24 sm:pt-28 lg:pb-24">
          <div className="landing-hero-theme-art pointer-events-none absolute inset-0" aria-hidden="true">
            <picture className="landing-hero-theme-art__picture">
              <source media="(max-width: 600px)" srcSet={landingHeroMobileAsset} />
              <img
                className="landing-hero-theme-art__image"
                src={landingHeroDesktopAsset}
                alt=""
                decoding="async"
                fetchPriority="high"
              />
            </picture>
          </div>

          <div className="landing-hero-grid relative mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[0.94fr_1.06fr] lg:items-start lg:gap-16">
            <div className="landing-hero-copy">

              <h1 className="landing-hero-title max-w-3xl text-[2.7rem] font-semibold leading-[1.01] tracking-[-0.055em] sm:text-6xl lg:text-7xl">
                <span className="block">Never lose customer context</span>
                <span className="landing-hero-promise block">from one call to the next.</span>
              </h1>

              <p className="landing-hero-subcopy marketing-lead-copy mt-7 max-w-2xl">
                Callsect instantly turns your customer conversations into everything you need after the call, shaped around how you write and speak.
              </p>

            </div>

            <LandingAudienceDiagram />
          </div>

          <div className="landing-hero-footer relative mx-auto max-w-7xl">
            <div className="landing-hero-actions">
              {user ? (
                <Link
                  to="/dashboard"
                  onClick={() => trackSignIn("hero-secondary")}
                  className="landing-signup-primary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition"
                >
                  <span>Open dashboard</span>
                  <ArrowRight size={17} weight="bold" />
                </Link>
              ) : (
                <Link
                  to="/login?mode=register"
                  onClick={() => trackSignUp("hero")}
                  className="landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition"
                >
                  <span>Sign up free</span>
                </Link>
              )}

              <div className="landing-demo-stack">
                <a
                  href="#product-tour"
                  onClick={scrollToProductTour}
                  className="landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition"
                >
                  <Play size={17} weight="fill" aria-hidden="true" />
                  <span>Watch product tour</span>
                </a>
              </div>
            </div>

            <div className="landing-below-ctas">
              <span className="landing-supports__label landing-built-for__label">Built for:</span>
              <div className="landing-built-for" aria-label="Who Callsect is built for">
                <span className="landing-built-for__text">Customer-facing professionals managing relationships across multiple calls</span>
              </div>
              <span className="landing-supports__label landing-supports__label--works">Works with:</span>
              <div className="landing-supports" aria-label="Meeting integration availability">
                <span className="landing-platform landing-platform--meet">
                  <img className="landing-platform-logo landing-meet-logo" src={googleMeetLogo} alt="" aria-hidden="true" />
                  <strong>Google Meet</strong>
                  <i className="landing-platform__active" aria-label="Active" />
                </span>
                <span className="landing-platform landing-platform--zoom" aria-label="Zoom, coming">
                  <img className="landing-platform-logo landing-zoom-logo" src={zoomLogo} alt="" aria-hidden="true" />
                  <em>coming</em>
                </span>
                <span className="landing-platform landing-platform--teams">
                  <img className="landing-platform-logo landing-teams-logo" src={microsoftTeamsLogo} alt="" aria-hidden="true" />
                  <strong>Microsoft Teams</strong>
                  <em>coming</em>
                </span>
              </div>
            </div>
          </div>
        </section>

        <LandingPromoVideoSection onExploreDemo={() => trackDemo("promo-video")} />

        <LandingRelationshipMemoryProof />

        <section className="px-5 py-16 sm:px-8 sm:py-24">
          <div className="marketing-trust-panel mx-auto grid max-w-7xl gap-8 rounded-2xl border p-8 sm:p-12 lg:grid-cols-[0.78fr_1.22fr] lg:items-center">
            <div>
              <div className="marketing-success-badge flex h-11 w-11 items-center justify-center rounded-xl border">
                <ShieldCheck size={23} />
              </div>
              <h2 className="mt-7 text-3xl font-semibold tracking-tight sm:text-4xl">
                Your customer calls stay yours.
              </h2>
            </div>

            <div>
              <p className="marketing-lead-copy max-w-3xl">
                Callsect keeps only what it needs to help you after the conversation. Your data stays under your control.
              </p>

              <div className="landing-trust-statements mt-7 grid gap-x-8 gap-y-3 text-sm sm:grid-cols-3">
                <TrustPoint>No bots in your calls</TrustPoint>
                <TrustPoint>No audio retained</TrustPoint>
                <TrustPoint>You control your data</TrustPoint>
              </div>

              <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <Link className="marketing-inline-accent-link" to="/privacy">
                  Privacy Notice
                </Link>
                <Link
                  className="marketing-inline-accent-link"
                  to="/data-handling"
                >
                  Data handling
                </Link>
                <Link className="marketing-inline-accent-link" to="/terms">
                  Terms
                </Link>
              </div>
            </div>
          </div>
        </section>

        <LandingFaqSection onContact={() => openSupportDialog("faq")} />

        <section className="px-5 pb-20 pt-10 sm:px-8 sm:pb-24 sm:pt-16">
          <div className="marketing-final-panel relative mx-auto overflow-hidden rounded-2xl border px-8 py-14 text-center sm:px-12 sm:py-20">
            <div className="relative mx-auto max-w-4xl">
              <h2 className="text-3xl font-semibold tracking-[-0.045em] sm:text-5xl lg:text-6xl">
                See the call clearly.
                <span className="landing-final-cta__gradient mt-2 block">
                  Carry the understanding forward.
                </span>
              </h2>
              <p className="marketing-lead-copy mx-auto mt-6 max-w-2xl">
                Relationship memory, CRM-ready notes, evidence-backed coaching, and next-call preparation—
                connected around the same customer conversation.
              </p>

              <Link
                to="/login?mode=register"
                onClick={() => trackSignUp("final-cta")}
                className="landing-signup-primary-cta mt-9 inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition"
              >
                <span>Sign up free</span>
              </Link>

              <div className="mt-4 text-sm marketing-text-faint">Currently free to use · No credit card required</div>
            </div>
          </div>
        </section>
      </main>

      <footer className="marketing-footer border-t px-5 py-10 sm:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <BrandLockup className="brand-lockup--footer" variant="auto" />
            <span>© 2026 Callsect</span>
          </div>

          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/privacy">
              Privacy
            </Link>
            <Link to="/terms">
              Terms
            </Link>
            <Link to="/data-handling">
              Data handling
            </Link>
            <Link to="/about">
              About
            </Link>
            <button
              type="button"
             
              onClick={() => openSupportDialog("footer")}
            >
              Support
            </button>
          </div>
        </div>
      </footer>

      {contactDialog ? (
        <PublicContactDialog
          kind={contactDialog}
          open
          initialEmail={contactDialog === "beta" ? requestedEmail : ""}
          onClose={closeContactDialog}
        />
      ) : null}
    </div>
  );
}



function LandingPromoVideoSection({ onExploreDemo }) {
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const hasTrackedPlayRef = useRef(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [keyboardControlsActive, setKeyboardControlsActive] = useState(false);
  const controlsHideTimerRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(35);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => setFullscreen(document.fullscreenElement === playerRef.current);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => () => {
    if (controlsHideTimerRef.current) window.clearTimeout(controlsHideTimerRef.current);
  }, []);

  const clearControlsHideTimer = () => {
    if (!controlsHideTimerRef.current) return;
    window.clearTimeout(controlsHideTimerRef.current);
    controlsHideTimerRef.current = null;
  };

  const scheduleControlsHide = () => {
    clearControlsHideTimer();
    if (!playing) return;
    controlsHideTimerRef.current = window.setTimeout(() => {
      setControlsVisible(false);
      controlsHideTimerRef.current = null;
    }, 1250);
  };

  const revealControls = () => {
    setControlsVisible(true);
    scheduleControlsHide();
  };

  const handlePlay = () => {
    setHasStarted(true);
    setPlaying(true);
    setControlsVisible(true);

    if (hasTrackedPlayRef.current) return;
    hasTrackedPlayRef.current = true;
    trackPublicEvent("cta_click", {
      source: "landing",
      section: "promo-video",
      cta_id: "promo_video_play",
    });
  };

  useEffect(() => {
    clearControlsHideTimer();
    if (playing) {
      setControlsVisible(true);
      controlsHideTimerRef.current = window.setTimeout(() => {
        setControlsVisible(false);
        controlsHideTimerRef.current = null;
      }, 1250);
    } else {
      setControlsVisible(true);
    }
    return clearControlsHideTimer;
  }, [playing]);

  const startVideo = () => {
    videoRef.current?.play().catch(() => {
      // Keep the intentional play control available if the browser prevents playback.
      setHasStarted(false);
      setPlaying(false);
    });
  };

  const togglePlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => setPlaying(false));
    } else {
      video.pause();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setMuted(video.muted);
    revealControls();
  };

  const changeVolume = (event) => {
    const video = videoRef.current;
    if (!video) return;
    const nextVolume = Math.min(1, Math.max(0, Number(event.target.value)));
    video.volume = nextVolume;
    video.muted = nextVolume === 0;
    setVolume(nextVolume);
    setMuted(video.muted);
    revealControls();
  };

  const seekVideo = (event) => {
    const video = videoRef.current;
    if (!video) return;
    const nextTime = Number(event.target.value);
    video.currentTime = nextTime;
    setCurrentTime(nextTime);
  };

  const toggleFullscreen = async () => {
    const player = playerRef.current;
    const video = videoRef.current;
    if (!player || !video) return;

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen?.();
      } else if (player.requestFullscreen) {
        await player.requestFullscreen();
      } else if (video.webkitEnterFullscreen) {
        video.webkitEnterFullscreen();
      }
    } catch {
      // Fullscreen remains optional when the browser or embedding context blocks it.
    }
  };

  const formatVideoTime = (seconds) => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const remainder = safeSeconds % 60;
    return `${minutes}:${String(remainder).padStart(2, "0")}`;
  };

  return (
    <section id="product-tour" className="landing-promo-video marketing-band border-y px-5 py-16 sm:px-8 sm:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-4xl text-center">
          <LandingReveal>
            <Eyebrow className="justify-center">Callsect in 35 seconds</Eyebrow>
          </LandingReveal>
          <LandingReveal delay={80}>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
              See what happens after the call.
            </h2>
          </LandingReveal>
          <LandingReveal delay={150}>
            <p className="mx-auto mt-6 marketing-lead-copy max-w-3xl">
              From understanding what happened to preparing what comes next.
            </p>
          </LandingReveal>
        </div>

        <LandingReveal delay={220} className="mt-10 sm:mt-12">
          <div
            ref={playerRef}
            className="landing-promo-video__player"
            onPointerMove={hasStarted ? revealControls : undefined}
            onPointerDown={hasStarted ? () => {
              setKeyboardControlsActive(false);
              revealControls();
            } : undefined}
            onFocusCapture={hasStarted ? (event) => {
              const keyboardFocus = typeof event.target?.matches === "function" && event.target.matches(":focus-visible");
              setKeyboardControlsActive(keyboardFocus);
              setControlsVisible(true);
              if (keyboardFocus) clearControlsHideTimer();
              else scheduleControlsHide();
            } : undefined}
            onBlurCapture={hasStarted ? (event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setKeyboardControlsActive(false);
                scheduleControlsHide();
              }
            } : undefined}
          >
            <video
              ref={videoRef}
              className="landing-promo-video__media"
              playsInline
              preload="metadata"
              poster="/callsect-promo-video-poster.jpg"
              aria-label="Callsect product overview video"
              onClick={hasStarted ? togglePlayback : startVideo}
              onLoadedMetadata={(event) => setDuration(Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 35)}
              onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
              onPlay={handlePlay}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              onVolumeChange={(event) => {
                setMuted(event.currentTarget.muted);
                setVolume(event.currentTarget.volume);
              }}
            >
              <source src="/callsect-promo-video.mp4" type="video/mp4" />
            </video>

            {!hasStarted ? (
              <button
                type="button"
                className="landing-promo-video__play"
                onClick={startVideo}
                aria-label="Play the Callsect product overview"
              >
                <span className="landing-promo-video__play-icon" aria-hidden="true">
                  <Play size={20} weight="fill" />
                </span>
                <span>
                  <strong>Play the overview</strong>
                  <small>00:35 · Sound on</small>
                </span>
              </button>
            ) : (
              <div
                className={`landing-promo-video__controls${controlsVisible || !playing ? " is-visible" : ""}${keyboardControlsActive ? " is-keyboard-focus" : ""}`}
                role="group"
                aria-label="Video controls"
                onPointerEnter={() => { clearControlsHideTimer(); setControlsVisible(true); }}
                onPointerLeave={scheduleControlsHide}
              >
                <button
                  type="button"
                  className="landing-promo-video__control-button"
                  onClick={togglePlayback}
                  aria-label={playing ? "Pause product overview" : "Play product overview"}
                  title={playing ? "Pause" : "Play"}
                >
                  {playing ? <Pause size={17} weight="fill" aria-hidden="true" /> : <Play size={17} weight="fill" aria-hidden="true" />}
                </button>
                <span className="landing-promo-video__time" aria-hidden="true">
                  {formatVideoTime(currentTime)} / {formatVideoTime(duration)}
                </span>
                <input
                  className="landing-promo-video__scrubber"
                  type="range"
                  min="0"
                  max={Math.max(duration, 0)}
                  step="0.1"
                  value={Math.min(currentTime, duration || 0)}
                  onChange={seekVideo}
                  aria-label="Video time scrubber"
                  aria-valuetext={`${formatVideoTime(currentTime)} of ${formatVideoTime(duration)}`}
                />
                <div className="landing-promo-video__volume-control">
                  <button
                    type="button"
                    className="landing-promo-video__control-button"
                    onClick={toggleMute}
                    aria-label={muted ? "Unmute product overview" : "Mute product overview"}
                    title={muted ? "Unmute" : "Mute"}
                  >
                    {muted || volume === 0 ? <SpeakerSlash size={18} aria-hidden="true" /> : <SpeakerHigh size={18} aria-hidden="true" />}
                  </button>
                  <input
                    className="landing-promo-video__volume-slider"
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={muted ? 0 : volume}
                    onChange={changeVolume}
                    aria-label="Product overview volume"
                    aria-valuetext={`${Math.round((muted ? 0 : volume) * 100)}%`}
                  />
                </div>
                <button
                  type="button"
                  className="landing-promo-video__control-button"
                  onClick={toggleFullscreen}
                  aria-label={fullscreen ? "Exit fullscreen product overview" : "Enter fullscreen product overview"}
                  title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
                >
                  <CornersOut size={18} aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </LandingReveal>

        <LandingReveal delay={280}>
          <div className="mt-7 flex justify-center">
            <div className="landing-promo-video__demo-stack">
              <Link
                to="/demo"
                onClick={onExploreDemo}
                className="landing-promo-video__demo-button inline-flex items-center justify-center gap-2"
              >
                <span>See Demo</span>
                <ArrowRight size={16} weight="bold" />
              </Link>
              <small className="landing-promo-video__demo-note">No signup required</small>
            </div>
          </div>
        </LandingReveal>
      </div>
    </section>
  );
}

function LandingReveal({ children, className = "", delay = 0, as = "div", stagger = false }) {
  const reducedMotion = useReducedMotion();
  const Tag = as;

  if (reducedMotion) {
    return (
      <Tag className={`landing-scroll-reveal${className ? ` ${className}` : ""}`}>
        {children}
      </Tag>
    );
  }

  const MotionTag = as === "article" ? motion.article : motion.div;
  const customDelay = Math.max(0, delay) / 1000;

  if (stagger) {
    return (
      <MotionTag
        className={`landing-scroll-reveal${className ? ` ${className}` : ""}`}
        variants={landingStaggerContainerVariants}
        custom={customDelay}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2, margin: "0px 0px -8% 0px" }}
      >
        {children}
      </MotionTag>
    );
  }

  return (
    <MotionTag
      className={`landing-scroll-reveal${className ? ` ${className}` : ""}`}
      variants={landingRevealVariants}
      custom={customDelay}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.22, margin: "0px 0px -8% 0px" }}
    >
      {children}
    </MotionTag>
  );
}

const landingRevealEase = [0.22, 1, 0.36, 1];

const landingRevealVariants = {
  hidden: {
    opacity: 0,
    y: 24,
  },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.72,
      delay,
      ease: landingRevealEase,
    },
  }),
};

const landingStaggerContainerVariants = {
  hidden: {},
  visible: (delay = 0) => ({
    transition: {
      delayChildren: delay,
      staggerChildren: 0.16,
    },
  }),
};

const landingCardVariants = {
  hidden: {
    opacity: 0,
    y: 28,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.76,
      ease: landingRevealEase,
    },
  },
};

function LandingSectionChapter({ number, children }) {
  return (
    <div className="landing-section-chapter">
      {number && <span className="landing-section-chapter__number" aria-hidden="true">{number}</span>}
      <span className="landing-section-chapter__label">{children}</span>
    </div>
  );
}


function LandingUnderstandSection() {
  return (
    <section className="landing-product-section px-5 py-16 sm:px-8 sm:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="landing-product-section__intro mx-auto max-w-4xl text-center">
          <LandingReveal>
            <LandingSectionChapter number="01">Understand</LandingSectionChapter>
          </LandingReveal>
          <LandingReveal delay={80}>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
              Know what was said.
              <span className="mt-2 block">
                Understand what it meant.
              </span>
            </h2>
          </LandingReveal>
          <LandingReveal delay={150}>
            <p className="mx-auto mt-6 marketing-lead-copy max-w-3xl">
              A summary compresses a call. Callsect reconstructs its commercial logic and shows the evidence behind each conclusion.
            </p>
          </LandingReveal>
        </div>

        <LandingReveal delay={190} className="landing-example-context mt-10 sm:mt-12">Sales example</LandingReveal>

        <LandingReveal delay={240} stagger className="landing-deep-dive-grid mt-5 sm:mt-6">
          <motion.article variants={landingCardVariants} className="landing-product-preview">
            <div className="landing-product-preview__header">
              <div>
                <span className="landing-product-preview__eyebrow">Deep Dive</span>
                <h3>What actually happened</h3>
              </div>
              <span className="landing-product-preview__status"><i />Evidence</span>
            </div>

            <LandingFindingPreview
              label="Objection"
              finding="Finance does not yet believe the problem is urgent enough to deserve budget this quarter."
              moments={[
                {
                  timestamp: "1:03",
                  quote: "We have a big onboarding project starting in September, so she is asking why this should compete for budget this quarter.",
                  interpretation: "The objection is prioritization against another funded initiative, not simple price resistance.",
                },
              ]}
            />

            <LandingFindingPreview
              label="Opportunity"
              finding="A short Finance meeting using Northstar's own baseline can turn an abstract priority objection into a real go/no-go decision."
              moments={[
                {
                  timestamp: "6:23",
                  quote: "Can we get twenty minutes with her Friday and use your last quarter as the baseline instead of sending another deck?",
                  interpretation: "The rep replaces asynchronous internal selling with direct access to the economic decision.",
                },
                {
                  timestamp: "9:33",
                  quote: "I will send the security pack today, and Friday we decide if the ROI clears the bar. If it does, we start Security the same day.",
                  interpretation: "Finance validation and Security are finally connected into one dated decision path.",
                },
              ]}
            />
          </motion.article>

          <motion.article variants={landingCardVariants} className="landing-product-preview">
            <div className="landing-product-preview__header">
              <div>
                <span className="landing-product-preview__eyebrow">Deep Dive</span>
                <h3>What it really means</h3>
              </div>
              <span className="landing-product-preview__status"><i />Interpretation</span>
            </div>

            <LandingFindingPreview
              label="Stance"
              finding="The champion is still engaged, but Finance has shifted the decision from product fit to proving urgency and measurable value."
              moments={[
                {
                  timestamp: "0:20",
                  quote: "The reaction was positive overall, but Rachel in Finance came back with a pretty basic question: why does this need to happen now?",
                  interpretation: "Positive product reaction is not translating into a funded priority.",
                },
                {
                  timestamp: "9:56",
                  quote: "That is better. Send me the pack by four and I will get Rachel's assistant to hold eleven Friday.",
                  interpretation: "The champion stays engaged once the next step becomes a decision conversation rather than another product presentation.",
                },
              ]}
            />

            <LandingFindingPreview
              label="Risk"
              finding="The deal can slip even with a positive champion because urgency was never established with the economic approver."
              moments={[
                {
                  timestamp: "5:58",
                  quote: "I shared your deck and she basically said, nice, but why now?",
                  interpretation: "The internal handoff exposed a missing business case that the product demo had temporarily hidden.",
                },
              ]}
            />
          </motion.article>
        </LandingReveal>
      </div>
    </section>
  );
}

function LandingFindingPreview({ label, finding, moments }) {
  return (
    <div className="landing-finding-preview">
      <div className="landing-finding-preview__head">
        <div>
          <span>{label}</span>
          <strong>{finding}</strong>
        </div>
      </div>

      <div className="landing-finding-preview__moments">
        {moments.map((moment) => (
          <div key={`${label}-${moment.timestamp}`} className="landing-evidence-preview">
            <div className="landing-evidence-preview__quote">
              <time>{moment.timestamp}</time>
              <p>“{moment.quote}”</p>
            </div>
            <p className="landing-evidence-preview__interpretation">{moment.interpretation}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function LandingImproveSection() {
  return (
    <section className="landing-product-section marketing-band border-y px-5 py-16 sm:px-8 sm:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="landing-product-section__intro mx-auto max-w-4xl text-center">
          <LandingReveal>
            <LandingSectionChapter number="02">Improve</LandingSectionChapter>
          </LandingReveal>
          <LandingReveal delay={80}>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
              Not a scorecard.
              <span className="mt-2 block">
                A second look at your decisions.
              </span>
            </h2>
          </LandingReveal>
          <LandingReveal delay={150}>
            <p className="mx-auto mt-6 marketing-lead-copy max-w-3xl">
              See what you could have said differently, and why it might have changed the conversation.
            </p>
          </LandingReveal>
        </div>

        <LandingReveal delay={190} className="landing-example-context mt-10 sm:mt-12">Sales example</LandingReveal>

        <LandingReveal delay={240} stagger className="landing-coaching-grid mt-5 sm:mt-6">
          <motion.article variants={landingCardVariants} className="landing-product-preview landing-coaching-preview">
            <div className="landing-product-preview__header">
              <div>
                <span className="landing-product-preview__eyebrow">Coaching Move</span>
                <h3>What could you have done better?</h3>
              </div>
              <span className="landing-product-preview__status"><i />Guidance</span>
            </div>

            <LandingCoachingField label="Situation">
              <div className="landing-coaching-evidence-block">
                <LandingCoachingEvidence speaker="CLIENT" tone="client">
                  “I know, and I liked the demo. But I do not have a number I can put in front of her.”
                </LandingCoachingEvidence>
                <p className="landing-coaching-field__observation">Maya is being asked to carry the business case to Finance without a quantified baseline or direct access.</p>
              </div>
            </LandingCoachingField>
            <LandingCoachingField label="Better approach">
              <strong className="landing-coaching-field__finding">Do not outsource the internal sale to your champion.</strong>
              <p>Before you send collateral for internal forwarding, identify what the absent stakeholder must believe, what evidence they will trust, and whether you can join that conversation directly.</p>
            </LandingCoachingField>
            <LandingCoachingField label="Example wording" emphasized>
              “Before I give you another deck to carry internally, what is Rachel going to need to believe for this to become a priority, and can we test that with her directly?”
            </LandingCoachingField>
            <LandingCoachingField label="Why it works">
              It prevents positive champion sentiment from masquerading as deal progress and exposes whether you actually have access to the decision process.
            </LandingCoachingField>
          </motion.article>

          <motion.article variants={landingCardVariants} className="landing-product-preview landing-coaching-preview">
            <div className="landing-product-preview__header">
              <div>
                <span className="landing-product-preview__eyebrow">Next conversation</span>
                <h3>Turn the critique into your next move</h3>
              </div>
              <span className="landing-product-preview__status"><i />Strategy</span>
            </div>

            <LandingCoachingField label="Missed opportunity">
              <div className="landing-coaching-evidence-block">
                <LandingCoachingEvidence speaker="YOU" tone="rep">
                  “We can send over the ROI calculator and some benchmarks from similar teams.”
                </LandingCoachingEvidence>
                <p className="landing-coaching-field__observation">You reached for generic collateral before understanding whether Finance was questioning price, priority, or measurable outcome.</p>
              </div>
            </LandingCoachingField>
            <LandingCoachingField label="Question to ask" emphasized>
              “What would Rachel need to believe for this to be worth doing this quarter?”
            </LandingCoachingField>
            <LandingCoachingField label="Negotiation suggestion">
              Do not negotiate against an unqualified priority objection. Make the cost of delay, six-month payback threshold, and approval sequence explicit before discussing commercial terms.
            </LandingCoachingField>
          </motion.article>
        </LandingReveal>
      </div>
    </section>
  );
}

function LandingCoachingEvidence({ speaker, tone, children }) {
  return (
    <span className="landing-coaching-evidence">
      <span className={`landing-coaching-evidence__speaker is-${tone}`}>{speaker}</span>
      <span className="landing-coaching-field__quote">{children}</span>
    </span>
  );
}

function LandingCoachingField({ label, children, emphasized = false }) {
  return (
    <div className={`landing-coaching-field${emphasized ? " is-emphasized" : ""}`}>
      <span>{label}</span>
      <div>{children}</div>
    </div>
  );
}

function LandingPrepareNextPreview() {
  const prep = DEMO_RELATIONSHIP_DATA.relationship_memory?.prepare_next_call || {};
  const objective = prep.objective?.text || "";
  const objections = Array.isArray(prep.likely_objections) ? prep.likely_objections.slice(0, 2) : [];
  const callFlow = Array.isArray(prep.suggested_call_flow) ? prep.suggested_call_flow.slice(0, 4) : [];
  const questions = Array.isArray(prep.questions_to_ask) ? prep.questions_to_ask.slice(0, 2) : [];

  return (
    <>
      <div className="landing-product-preview__header">
        <div>
          <span className="landing-product-preview__eyebrow">Prepare Next Call</span>
          <h3>Walk in with a plan, not just a recap.</h3>
        </div>
        <span className="landing-product-preview__status"><i />Action</span>
      </div>

      <section className="landing-prepare-next-preview__strategy">
        <span>Plan</span>
        <strong>{prep.conversation_strategy}</strong>
      </section>

      <div className="landing-prepare-next-preview__pair">
        <section className="landing-prepare-next-preview__card">
          <span>Your Goal</span>
          <strong>{objective}</strong>
        </section>

        <section className="landing-prepare-next-preview__card">
          <span>Likely objections</span>
          <div className="landing-prepare-next-preview__objections">
            {objections.map((item) => (
              <div key={item.text}>
                <strong>{item.text}</strong>
                {item.response_direction ? <small>{item.response_direction}</small> : null}
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="landing-prepare-next-preview__flow">
        <span>Call flow tips</span>
        <ol>
          {callFlow.map((item, index) => (
            <li key={item.title}>
              <b>{index + 1}</b>
              <div>
                <strong>{item.title}</strong>
                <small>{item.purpose}</small>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="landing-prepare-next-preview__question">
        <span>Questions to ask</span>
        <div className="landing-prepare-next-preview__question-list">
          {questions.map((item) => (
            <blockquote key={item.text}>“{item.text}”</blockquote>
          ))}
        </div>
      </section>
    </>
  );
}

function LandingActSection() {
  return (
    <section className="landing-product-section px-5 py-16 sm:px-8 sm:py-24">
      <div className="mx-auto max-w-7xl">
        <LandingReveal>
          <LandingSectionChapter number="04">Act</LandingSectionChapter>
        </LandingReveal>

        <div className="landing-act-layout mt-10 grid gap-12 sm:mt-12 lg:grid-cols-[0.76fr_1.24fr] lg:items-start lg:gap-x-16 lg:gap-y-6">
          <LandingReveal delay={90}>
            <h2 className="mt-4 max-w-xl text-3xl font-semibold tracking-[-0.04em] sm:text-5xl">
              The analysis is useful.
              <span className="mt-2 block">
                Turn it into what happens next.
              </span>
            </h2>
            <p className="marketing-lead-copy mt-6 max-w-xl">
              Close the loop after the call with CRM documentation in your voice, then walk into the next conversation with the questions, missed opportunities, and negotiation context that matter.
            </p>
            <div className="landing-act-chips mt-7" aria-label="Callsect action outputs">
              <TrustPoint>CRM-ready</TrustPoint>
              <TrustPoint>Next-call prep</TrustPoint>
              <TrustPoint>Writing DNA</TrustPoint>
              <TrustPoint>Speaking DNA</TrustPoint>
            </div>
            <p className="mt-6 max-w-xl text-sm leading-7 marketing-text-faint">
              The original problem that started Callsect was better notes from real customer calls. The bigger opportunity was making sure the useful context does not disappear before the next one.
            </p>
          </LandingReveal>

          <div className="landing-act-example-stack">
            <LandingReveal delay={140} className="landing-example-context landing-act-example-context">AM · CSM example</LandingReveal>
            <LandingReveal delay={160} as="article" className="landing-crm-preview landing-act-crm-preview">
              <div className="landing-product-preview__header">
                <div>
                  <span className="landing-product-preview__eyebrow">CRM note</span>
                  <h3>Ready to save. Still sounds like a person.</h3>
                </div>
                <span className="landing-product-preview__status"><i />Documentation</span>
              </div>
              <div className="landing-crm-preview__body">
                <p><strong>Spoke with Dana ahead of the renewal.</strong> She was candid that the team likes the platform, but adoption is too low to justify another year: Operations uses it, Finance barely logs in, and Sales dropped off after the first month.</p>
                <p>The real blocker emerged when Dana explained that Priya, who owned the rollout, left in May. Dana inherited it without the bandwidth or authority to drive adoption. The CFO needs measurable value, not positive sentiment. I initially leaned too hard on features, then reframed around ownership and ROI.</p>
                <p>I will send a one-page value snapshot early Friday using current usage data, including the gaps, and we booked an ROI workshop for Monday at 2pm with Dana and Operations.</p>
              </div>
            </LandingReveal>
          </div>

          <LandingReveal delay={220} as="article" className="landing-product-preview landing-prepare-next-preview landing-act-prepare-wide">
            <LandingPrepareNextPreview />
          </LandingReveal>
        </div>
      </div>
    </section>
  );
}

function LandingRelationshipMemoryProof() {
  return (
    <section className="landing-relationship-proof marketing-band border-b px-5 py-14 sm:px-8 sm:py-20" aria-labelledby="landing-relationship-proof-title">
      <div className="mx-auto max-w-7xl">
        <div className="landing-relationship-proof__intro">
          <Eyebrow className="landing-relationship-proof__eyebrow">Relationship intelligence</Eyebrow>
          <h2 id="landing-relationship-proof-title" className="landing-relationship-proof__title">
            One call tells you what happened. The history tells you what it means.
          </h2>
          <p className="landing-relationship-proof__supporting marketing-lead-copy">
            Callsect connects what happens across calls, so the context that matters doesn't disappear into separate transcripts and summaries.
          </p>
        </div>

        <LandingConversationHistoryPreview />

        <p className="landing-relationship-proof__closing">
          Calls 1, 2 and 3 aren't three transcripts. They're one evolving relationship.
        </p>
      </div>
    </section>
  );
}

function LandingAudienceDiagram() {
  return (
    <aside className="landing-audience" aria-labelledby="landing-audience-title">
      <div className="landing-audience__intro">
        <span id="landing-audience-title" className="landing-audience__kicker"><GearSix size={20} weight="duotone" /> How it works</span>
      </div>

      <div className="landing-audience__capabilities">
        <div className="landing-audience__flow" aria-label="What goes into Callsect and what you get back">
          <div className="landing-audience__flow-stage">
            <span className="landing-audience__flow-label">What goes in</span>
            <div className="landing-audience__flow-items">
              <span className="landing-audience__flow-item">
                <span className="landing-audience__flow-icon" aria-hidden="true"><GoogleLogo size={17} weight="bold" /></span>
                <span className="landing-audience__flow-copy">Google Meet</span>
                <LandingInputInfo
                  label="How Google Meet import works"
                  title="No bot or extension."
                  body="Connect Google Meet once. Callsect automatically imports your meeting transcript after the call."
                />
              </span>
              <span className="landing-audience__flow-item">
                <span className="landing-audience__flow-icon" aria-hidden="true"><SpeakerHigh size={17} weight="regular" /></span>
                <span className="landing-audience__flow-copy">Transcript or recording</span>
                <LandingInputInfo
                  label="How transcript or recording upload works"
                  title="Already have the file?"
                  body="Upload a transcript, audio, or video directly to Callsect."
                />
              </span>
            </div>
          </div>

          <div className="landing-audience__flow-stage landing-audience__flow-stage--output">
            <span className="landing-audience__flow-label">What you get</span>
            <div className="landing-audience__output-grid">
              {audienceOutputs.map(({ label, icon: Icon, path, eventId }) => (
                <Link
                  className="landing-audience__output-link"
                  key={label}
                  to={path}
                  aria-label={`Learn more about ${label}`}
                  onClick={() => trackPublicEvent("cta_click", { source: "landing", section: "hero_capabilities", cta_id: eventId })}
                >
                  <span className="landing-audience__flow-icon" aria-hidden="true"><Icon size={16} weight="regular" /></span>
                  <span className="landing-audience__flow-copy">{label}</span>
                  <span className="landing-audience__output-arrow" aria-hidden="true"><ArrowRight size={12} weight="bold" /></span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <p className="landing-audience__workspace-note">
          Customer histories stay organized in Callsect — <strong>no CRM required.</strong>
        </p>
      </div>
    </aside>
  );
}

function LandingInputInfo({ label, title, body }) {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();

  return (
    <span className="landing-audience__input-info" data-open={open ? "true" : "false"}>
      <button
        type="button"
        className="landing-audience__input-info-trigger"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={tooltipId}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            setOpen(false);
            event.currentTarget.blur();
          }
        }}
      >
        <Info size={13} weight="bold" aria-hidden="true" />
      </button>
      <span id={tooltipId} className="landing-audience__input-info-popover" role="tooltip">
        <strong>{title}</strong> {body}
      </span>
    </span>
  );
}

function CallsectWorkflowFlow({ onOpen }) {
  const outputs = [
    {
      icon: Megaphone,
      iconWeight: "fill",
      label: "Coaching",
      copy: "A better approach, with wording and the reasoning behind it.",
    },
    {
      icon: FileText,
      label: "CRM note",
      copy: "Human context, ready to save in your writing style.",
    },
    {
      icon: User,
      label: "Relationship memory",
      copy: "Carry what Callsect learned into the customer relationship.",
    },
  ];

  return (
    <article
      className="workflow-glance"
      aria-label="The Callsect workflow at a glance"
    >
      <div className="workflow-glance__glow" aria-hidden="true" />

      <header className="workflow-glance__header">
        <h2>
          The Callsect workflow
          <span>at a glance.</span>
        </h2>
        <p>From conversation to understanding to action.</p>
      </header>

      <div className="workflow-glance__body">
        <section className="workflow-glance__card workflow-glance__call">
          <div className="workflow-glance__icon" aria-hidden="true">
            <PhoneCall size={31} weight="fill" />
          </div>
          <div className="workflow-glance__card-copy">
            <h3>Your customer call</h3>
            <p
              className="marketing-workflow-subcopy"
              style={{
                margin: "0.55rem 0 0",
                fontSize: "0.78rem",
                fontWeight: 430,
                lineHeight: 1.48,
              }}
            >
              Start with a recording or transcript you already have.
            </p>
            <div
              className="workflow-glance__chips"
              aria-label="Call input and privacy controls"
            >
              {[
                "Doesn't record your calls",
                "Data can stay anonymous",
                "Delete anytime",
              ].map((item) => (
                <span
                  key={item}
                  className="marketing-privacy-chip"
                  style={{ whiteSpace: "nowrap" }}
                >
                  <i
                    aria-hidden="true"
                    className="marketing-privacy-dot"
                    style={{
                      width: "0.31rem",
                      height: "0.31rem",
                      flex: "0 0 auto",
                      borderRadius: "999px",
                      marginRight: "0.34rem",
                    }}
                  />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </section>

        <div className="workflow-glance__vertical-link" aria-hidden="true">
          <span />
        </div>

        <section className="workflow-glance__card workflow-glance__dissection">
          <div className="workflow-glance__icon" aria-hidden="true">
            <Target size={31} weight="fill" />
          </div>
          <div className="workflow-glance__card-copy">
            <h3>Dissection</h3>
            <p>Separate evidence, meaning, risk, and decision dynamics before deciding what to do next.</p>
            <div className="workflow-glance__chips" aria-label="Dissection dimensions">
              <span>Stance</span><span>Tone</span><span>Blockers</span><span>Objections</span><span>Risks</span><span>Opportunities</span>
            </div>
          </div>
        </section>

        <div className="workflow-glance__branch" aria-hidden="true">
          <span className="workflow-glance__branch-stem" />
          <span className="workflow-glance__branch-bar" />
          <span className="workflow-glance__branch-drop workflow-glance__branch-drop--left" />
          <span className="workflow-glance__branch-drop workflow-glance__branch-drop--center" />
          <span className="workflow-glance__branch-drop workflow-glance__branch-drop--right" />
        </div>

        <div className="workflow-glance__outputs">
          {outputs.map(({ icon: Icon, iconWeight = "bold", label, copy }) => (
            <section key={label} className="workflow-glance__output-card">
              <div className="workflow-glance__icon" aria-hidden="true">
                <Icon size={29} weight={iconWeight} />
              </div>
              <h3>{label}</h3>
              <p>{copy}</p>
            </section>
          ))}
        </div>

        <div className="workflow-glance__vertical-link workflow-glance__vertical-link--after-outputs" aria-hidden="true">
          <span />
        </div>

        <section className="workflow-glance__card workflow-glance__next-call">
          <div className="workflow-glance__icon" aria-hidden="true">
            <ChatCircleDots size={31} weight="fill" />
          </div>
          <div className="workflow-glance__card-copy">
            <h3>Next conversation</h3>
            <p>Questions, negotiation moves, and customer-specific preparation grounded in what actually happened.</p>
          </div>
        </section>
      </div>

      <footer className="workflow-glance__footer">
        <Link to="/demo" onClick={onOpen}>
          <span className="workflow-glance__footer-copy">
            <span>See how it works</span>
            <small>No signup required</small>
          </span>
          <ArrowRight size={23} weight="bold" />
        </Link>
      </footer>
    </article>
  );
}

function RelationshipUpdatePreview({ onOpen }) {
  const summaryItems = [
    "Low adoption shifted from a usage concern to a renewal justification problem.",
    "Dana revealed that the original rollout owner left, leaving no accountable internal sponsor.",
    "AxisLoop entered procurement with bundled adoption support.",
    "A transparent value snapshot and Monday ROI workshop created a conditional rescue path.",
  ];

  const signals = [
    {
      label: "Stance",
      from: "Renewal assumed",
      to: "Open, proof required",
      direction: "Changed",
    },
    {
      label: "Buying intent",
      from: 62,
      to: 45,
      direction: "Decreased",
    },
    {
      label: "Trust",
      from: 74,
      to: 82,
      direction: "Increased",
    },
    {
      label: "Tone",
      from: "Concerned",
      to: "Candid, skeptical",
      direction: "Changed",
    },
  ];

  return (
    <section
      className="longitudinal-delta longitudinal-delta--compact is-expanded landing-relationship-update"
      aria-label="SablePoint Logistics relationship update after the latest customer call"
    >
      <div className="longitudinal-delta__header">
        <div className="longitudinal-delta__toggle landing-relationship-update__title-shell">
          <span className="longitudinal-delta__title">
            <ClockCounterClockwise size={17} weight="duotone" />
            Relationship update
          </span>
        </div>

        <div className="longitudinal-delta__header-actions">
          <Link
            to="/demo/renewals/relationship"
            onClick={onOpen}
            className="longitudinal-delta__relationship-link"
            aria-label="Open the live SablePoint Logistics relationship context"
          >
            <span>Live relationship context</span>
            <ArrowRight size={14} weight="bold" aria-hidden="true" />
          </Link>
          <span className="longitudinal-delta__expand-button landing-relationship-update__caret" aria-hidden="true">
            <CaretDown size={17} weight="bold" className="longitudinal-delta__chevron" />
          </span>
        </div>
      </div>

      <div className="longitudinal-delta__content landing-relationship-update__content">
        <ul className="longitudinal-delta__summary longitudinal-delta__summary--compact">
          {summaryItems.map((item) => (
            <li key={item}>
              <TrendUp size={15} aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <div className="longitudinal-signal-grid" aria-label="Updated SablePoint Logistics relationship signals">
          {signals.map((signal) => (
            <div key={signal.label} className="longitudinal-signal">
              <div className="longitudinal-signal__label">{signal.label}</div>
              <div className="longitudinal-signal__value">
                <span className={landingSignalToneClass(signal.from, true)}>{scoreValueToRating(signal.from)}</span>
                <span className="marketing-transition-arrow mx-2">→</span>
                <span className={landingSignalToneClass(signal.to)}>{scoreValueToRating(signal.to)}</span>
              </div>
              <div className="longitudinal-signal__direction">{signal.direction}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


function LandingConversationHistoryPreview() {
  const [expanded, setExpanded] = useState(() => {
    if (typeof window === "undefined") return true;
    return !window.matchMedia("(max-width: 700px)").matches;
  });
  const milestones = [...DEMO_RELATIONSHIP_DATA.milestones].sort(
    (left, right) => new Date(right.date).getTime() - new Date(left.date).getTime()
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mobileQuery = window.matchMedia("(max-width: 700px)");
    const syncResponsiveState = (event) => setExpanded(!event.matches);

    setExpanded(!mobileQuery.matches);
    mobileQuery.addEventListener?.("change", syncResponsiveState);
    return () => mobileQuery.removeEventListener?.("change", syncResponsiveState);
  }, []);

  return (
    <section className="relationship-history landing-conversation-history landing-relationship-proof__history-preview" aria-label="SablePoint Logistics conversation history preview">
      <div className="relationship-history__header">
        <div>
          <h2>Conversation history</h2>
          <p>From the AM / CSM interactive demo</p>
        </div>
        <div className="relationship-history__actions">
          <span>{milestones.length} calls</span>
          <button
            type="button"
            className="relationship-history__toggle"
            onClick={() => setExpanded((current) => !current)}
            aria-expanded={expanded}
          >
            {expanded ? "Compact history" : "Expand history"}
            <CaretDown size={14} weight="bold" aria-hidden="true" />
          </button>
        </div>
      </div>

      <ConversationHistoryTimeline
        milestones={milestones}
        objectionEvolution={DEMO_RELATIONSHIP_DATA.objection_evolution}
        relationshipCallCount={milestones.length}
        activeCallId={DEMO_RELATIONSHIP_DATA.relationship_memory?.last_processed_call_id}
        allCollapsed={!expanded}
        getCallLink={(milestone) => ({
          to: "/demo/renewals/relationship",
          disabled: false,
          state: { activeCallId: milestone.call_id },
        })}
        renderFollowUpAction={(milestone) => (
          String(milestone.call_id) === String(DEMO_RELATIONSHIP_DATA.relationship_memory?.last_processed_call_id) ? (
            <span className="relationship-story-card__follow-up is-suggested landing-relationship-proof__follow-up-static" aria-label="Suggested follow-up">
              <EnvelopeSimple size={15} weight="duotone" aria-hidden="true" />
              <span>Suggested</span>
            </span>
          ) : (
            <span className="relationship-story-card__follow-up relationship-story-card__follow-up--state is-previous" aria-label="Previous call">
              <ClockCounterClockwise size={14} weight="bold" aria-hidden="true" />
              <span>Previous call</span>
            </span>
          )
        )}
        showCurrentBadge={false}
      />
    </section>
  );
}

function landingSignalToneClass(value, muted = false) {
  if (!isNumericScore(value)) return muted ? "marketing-text-faint" : "";
  return `score-text score-text--${scoreToTone(value)}`;
}

function LandingFaqSection({ onContact }) {
  const items = [
    {
      question: "How does Callsect get my conversations?",
      answer: (
        <>
          Connect Google Meet once and Callsect automatically imports the meeting transcript after the call. If you already have the conversation elsewhere, you can upload a transcript, audio, or video directly.
        </>
      ),
    },
    {
      question: "Does Callsect record or join my calls?",
      answer: (
        <>
          No. Callsect doesn&apos;t join your meetings as a bot and doesn&apos;t record your calls. With Google Meet, it reads the transcript generated after the meeting.
        </>
      ),
    },
    {
      question: "What happens to uploaded audio and my conversation data?",
      answer: (
        <>
          The original recording isn&apos;t stored in Callsect&apos;s database. Uploaded audio is processed for transcription and temporary copies are deleted after the transcription attempt. The resulting transcript and generated analysis stay in your account so Callsect can carry context forward. You can delete individual calls or your account data at any time. {" "}
          <Link to="/data-handling" className="landing-faq__inline-link">See exactly how data is handled.</Link>
        </>
      ),
    },
    {
      question: "What does “how you write and speak” mean?",
      answer: (
        <>
          Callsect learns two different kinds of patterns. Writing DNA learns from the writing examples you provide and shapes both CRM notes and follow-up emails around your structure, detail, and tone. Speaking DNA is learned from your calls and helps Callsect understand how you communicate and prepare you for the next conversation.
        </>
      ),
    },
    {
      question: "Do I need to change how I run my calls?",
      answer: (
        <>
          No. Callsect works after the conversation. It turns the calls you&apos;re already having into analysis, relationship context, notes, follow-ups, coaching, and preparation for the next one.
        </>
      ),
    },
    {
      question: "Is Callsect free?",
      answer: (
        <>
          Yes. Callsect is currently free to use, with usage limits. There are no paid plans yet, but paid plans will be introduced in the future.
        </>
      ),
    },
    {
      question: "Can my team use Callsect together?",
      answer: (
        <>
          Callsect is currently designed around individual customer-facing professionals and their customer relationships. Shared team workspaces aren&apos;t available yet.
        </>
      ),
    },
    {
      question: "Does Callsect integrate with our CRM?",
      answer: (
        <>
          Not currently. Callsect works without a CRM integration today. Native CRM integrations are something we&apos;re exploring for teams that need customer context to stay connected to their existing workflow.
        </>
      ),
    },
    {
      question: "Can my company use Callsect?",
      answer: (
        <>
          Callsect is currently self-serve for individual users. If you&apos;re interested in using it across a customer-facing team, {" "}
          <a href="#contact" className="landing-faq__inline-link" onClick={(event) => { event.preventDefault(); onContact(); }}>get in touch.</a>
        </>
      ),
    },
  ];

  return (
    <section className="landing-faq marketing-band border-y px-5 py-16 sm:px-8 sm:py-24" aria-labelledby="landing-faq-title">
      <div className="landing-faq__layout mx-auto max-w-7xl">
        <div className="landing-faq__intro">
          <Eyebrow>FAQ</Eyebrow>
          <h2 id="landing-faq-title">A few practical things worth knowing.</h2>
          <p className="marketing-lead-copy">
            How conversations get in, what Callsect keeps, how your communication style is used, and what free means right now.
          </p>
        </div>

        <div className="landing-faq__list">
          {items.map(({ question, answer }) => (
            <details key={question} className="landing-faq__item">
              <summary>
                <span>{question}</span>
                <CaretDown className="landing-faq__caret" size={18} weight="bold" aria-hidden="true" />
              </summary>
              <div className="landing-faq__answer">{answer}</div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}


function TrustPoint({ children }) {
  return (
    <span className="inline-flex items-center gap-2">
      <CheckCircle size={16} weight="fill" className="marketing-success-icon" />
      {children}
    </span>
  );
}


function Eyebrow({ children, className = "" }) {
  return (
    <div
      className={`landing-eyebrow marketing-eyebrow text-xs font-semibold uppercase tracking-[0.28em] ${className}`}
    >
      <span className="landing-eyebrow__lines" aria-hidden="true"><i /></span>
      <span>{children}</span>
    </div>
  );
}
